import React, { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { safeReturnPath } from "../auth/oauth";
import DocsLanguageSwitch from "../components/DocsLanguageSwitch";
import SepmLogo from "../components/SepmLogo";
import ThemeButton from "../components/ThemeButton";
import { useTheme } from "../context/ThemeContext";
import { siteCopy } from "../i18n/siteCopy";
import { useManualLocale } from "../manual/i18n/LocaleContext";

export default function LoginPage() {
  const { user, ready, signIn } = useAuth();
  const { isDark } = useTheme();
  const { locale } = useManualLocale();
  const copy = siteCopy[locale];
  const [params] = useSearchParams();
  const [error, setError] = useState(params.get("error") || "");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    document.title = copy.documentTitle;
  }, [copy.documentTitle]);
  const returnTo = safeReturnPath(params.get("return"));

  if (!ready) {
    return <div className="min-h-screen bg-gray-100 dark:bg-gray-900" />;
  }
  if (user) {
    return <Navigate to={returnTo} replace />;
  }

  const onSignIn = async () => {
    setError("");
    setPending(true);
    try {
      await signIn({ theme: isDark ? "dark" : "light", returnTo, lang: locale });
    } catch (err) {
      setPending(false);
      setError(err?.message || "Sign-in could not be started.");
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,0,21,0.12),transparent_55%)] dark:bg-[radial-gradient(ellipse_at_top,rgba(255,107,90,0.14),transparent_50%)]"
        aria-hidden="true"
      />

      <header className="relative z-10 flex items-center justify-end gap-2 px-4 py-4 md:px-8">
        <DocsLanguageSwitch />
        <ThemeButton className="hover:bg-white/70 dark:hover:bg-gray-800" />
      </header>

      <main className="relative z-10 flex min-h-[calc(100vh-4.5rem)] items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm rounded-3xl border border-gray-200/80 bg-white px-8 py-10 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <SepmLogo className="mx-auto h-16 w-16" />
          <h1 className="mt-5 text-xl font-semibold tracking-tight">
            <span className="text-sepm-primary dark:text-sepm-primary-dark">SEPM</span>{" "}
            {copy.documentation}
          </h1>

          {error ? (
            <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-left text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300" role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="button"
            onClick={onSignIn}
            disabled={pending}
            className="mt-6 w-full rounded-xl bg-sepm-primary py-3 font-semibold text-white transition hover:bg-sepm-primary-hover disabled:opacity-60"
          >
            {pending ? copy.redirecting : copy.signInButton}
          </button>
        </div>
      </main>
    </div>
  );
}
